import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  BarChart3,
  Maximize2,
  Minimize2,
  RotateCcw,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { deriveInsights } from "@/lib/viz/insights";

interface Props {
  /** The actual chart renderer — VizRenderer, ChartView, etc. */
  children: ReactNode;
  /**
   * Optional title shown in the chrome header. Falls back to "Visualization"
   * when blank so the header always has something to anchor the toolbar
   * buttons against.
   */
  title?: string;
  /**
   * Column names for the rendered dataset. When provided alongside
   * {@link Props#rows}, the chrome header surfaces a one-line insight
   * strip (row count, sum of the chosen numeric column, top
   * contributor). Optional — when omitted, the header is just title +
   * toolbar.
   */
  columnNames?: readonly string[];
  /** Row data backing the chart — same shape as VizRenderPayload.rows. */
  rows?: ReadonlyArray<ReadonlyArray<unknown>>;
}

/**
 * Chrome wrapper around a chart bubble — gradient border, header with
 * title + toolbar, and three interactive affordances:
 *
 *   • Zoom in / out / reset — CSS `transform: scale()` on the chart
 *     container, bounded between 0.5x and 3.0x. The transform doesn't
 *     resize the chart's intrinsic layout (axes don't reflow), so it
 *     reads as a "magnifying glass" rather than a layout refit — which
 *     is the right semantic when the user wants to peek at dense
 *     bars without scrolling.
 *   • Fullscreen — toggles a fixed-overlay portal-style div that
 *     covers the viewport. Falls back to a positioned overlay (not
 *     the browser's native Fullscreen API) because the chart needs to
 *     stay inside React's tree to keep handler refs alive — exiting
 *     native fullscreen mid-stream loses the resize observer.
 *
 * <p>Used by VisualBlock so every chart bubble gets the same chrome
 * without each renderer needing to know about it. The renderer just
 * paints into a fixed-size container and this wrapper handles the
 * frame.
 */
export function VizChrome({ children, title, columnNames, rows }: Props) {
  const [scale, setScale] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);

  // Memo the insights derivation so we don't re-scan rows on every
  // re-render (zoom, fullscreen toggle, etc.). The dataset is immutable
  // for a given chart bubble — rows/columns identity stays stable.
  const insights = useMemo(() => {
    if (!columnNames || !rows || rows.length === 0) return null;
    return deriveInsights(columnNames, rows);
  }, [columnNames, rows]);

  // Esc exits fullscreen — standard modal hygiene; without it the
  // overlay traps the user since clicking outside isn't possible
  // (the overlay covers everything).
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  const containerRef = useRef<HTMLDivElement>(null);

  // Click-drag panning. When the (zoomed-in or large) diagram overflows
  // its viewport, dragging scrolls the container — so the user can move
  // around an ERD that's bigger than the panel instead of hunting with
  // the scrollbars. We pan by adjusting scrollLeft/scrollTop (NOT a CSS
  // translate): it composes with the scale transform for free, and it
  // only engages when there's actually overflow to pan, so it never
  // steals drag from an interactive renderer (ECharts/DeckGL) that fits.
  const dragRef = useRef<{ x: number; y: number; sl: number; st: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [canPan, setCanPan] = useState(false);

  // Track whether the viewport currently overflows (→ pannable). Re-checks
  // on zoom and fullscreen toggle; a ResizeObserver catches window resizes.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const check = () =>
      setCanPan(el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scale, fullscreen]);

  const onPanDown = (e: React.MouseEvent) => {
    const el = containerRef.current;
    if (e.button !== 0 || !el) return;
    // Only pan when there's overflow — otherwise leave the event for the
    // chart/diagram underneath (clicks, tooltips, native selection).
    if (el.scrollWidth <= el.clientWidth + 1 && el.scrollHeight <= el.clientHeight + 1) return;
    dragRef.current = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
    setDragging(true);
    e.preventDefault(); // suppress text/SVG selection mid-drag
  };
  const onPanMove = (e: React.MouseEvent) => {
    const d = dragRef.current;
    const el = containerRef.current;
    if (!d || !el) return;
    el.scrollLeft = d.sl - (e.clientX - d.x);
    el.scrollTop = d.st - (e.clientY - d.y);
  };
  const onPanEnd = () => {
    dragRef.current = null;
    setDragging(false);
  };
  const panCursor = dragging ? "grabbing" : canPan ? "grab" : undefined;

  const zoomIn = () => setScale((s) => Math.min(3, +(s + 0.25).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2)));
  const reset = () => {
    setScale(1);
    containerRef.current?.scrollTo({ left: 0, top: 0 });
  };

  // Compose the chart body. Wrapped in a transform container so both
  // the inline and fullscreen modes get the zoom behaviour for free.
  const body = (
    <div
      ref={containerRef}
      className={`viz-chrome__body${dragging ? " select-none" : ""}`}
      // overflow:auto lets the user scroll a zoomed-in chart that
      // overflows its container — the alternative (clip) hides bars
      // off-screen with no way to see them. width:max-content gives
      // the transform something to grow into instead of staying
      // pinned to the original container width. Drag-to-pan (below)
      // moves this scroll viewport when the content overflows.
      style={{ overflow: "auto", cursor: panCursor }}
      onMouseDown={onPanDown}
      onMouseMove={onPanMove}
      onMouseUp={onPanEnd}
      onMouseLeave={onPanEnd}
    >
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          // When zoomed >1, width:max-content lets the scaled chart
          // claim the larger size it needs; the parent overflow:auto
          // then exposes the scrollbars.
          width: scale > 1 ? "max-content" : undefined,
          transition: "transform 120ms ease-out",
        }}
      >
        {children}
      </div>
    </div>
  );

  const toolbar = (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={zoomOut}
        disabled={scale <= 0.5}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-slate-500 transition hover:border-slate-200 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Zoom out"
        title="Zoom out"
      >
        <ZoomOut className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <span
        className="min-w-[3rem] text-center text-[11px] tabular-nums text-slate-500"
        aria-label={`Zoom level ${Math.round(scale * 100)} percent`}
      >
        {Math.round(scale * 100)}%
      </span>
      <button
        type="button"
        onClick={zoomIn}
        disabled={scale >= 3}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-slate-500 transition hover:border-slate-200 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Zoom in"
        title="Zoom in"
      >
        <ZoomIn className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={reset}
        disabled={scale === 1}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-slate-500 transition hover:border-slate-200 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Reset zoom"
        title="Reset to 100%"
      >
        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => setFullscreen((v) => !v)}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-slate-500 transition hover:border-slate-200 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
      >
        {fullscreen
          ? <Minimize2 className="h-3.5 w-3.5" aria-hidden="true" />
          : <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />}
      </button>
    </div>
  );

  // Insight chips strip — slots between the title and the toolbar.
  // Each chip is text-only, color-coded by role (count / measure /
  // top / null). Order matters: rows count is the anchor stat every
  // user wants first; sum + top come from the chosen numeric column
  // and may be absent on category-only datasets; null count surfaces
  // only when there's actually a data-quality concern (> 0).
  const chips = insights == null ? null : (
    <div
      className="hidden min-w-0 flex-1 items-center gap-1.5 truncate text-[11px] md:flex"
      aria-label="Quick insights"
    >
      <span
        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-700"
        title={`${insights.rowsTotal.toLocaleString()} rows × ${insights.colsTotal} columns`}
      >
        <span className="font-medium tabular-nums">{insights.rowsLabel}</span>
        <span className="text-slate-500">rows</span>
      </span>
      {insights.sumLabel && insights.measureColumn && (
        <span
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-700"
          title={`Sum of ${insights.measureColumn}`}
        >
          <span className="text-indigo-500">Σ</span>
          <span className="font-medium tabular-nums">{insights.sumLabel}</span>
          <span className="hidden text-indigo-400 lg:inline">
            {insights.measureColumn}
          </span>
        </span>
      )}
      {insights.topContributor && (
        <span
          className="inline-flex min-w-0 items-center gap-1 truncate rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700"
          title={`Top contributor by ${insights.measureColumn ?? "value"}`}
        >
          <span className="text-emerald-500">▲</span>
          <span className="truncate font-medium">{insights.topContributor.label}</span>
          <span className="shrink-0 text-emerald-500 tabular-nums">
            {insights.topContributor.sharePercent}
          </span>
        </span>
      )}
      {insights.nullCells > 0 && (
        <span
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700"
          title={`${insights.nullCells} empty/null cells across the dataset`}
        >
          <span className="text-amber-500">⊘</span>
          <span className="font-medium tabular-nums">{insights.nullCells}</span>
          <span className="hidden text-amber-500 lg:inline">nulls</span>
        </span>
      )}
    </div>
  );

  const header = (
    <header className="flex items-center justify-between gap-2 border-b border-slate-200/70 bg-gradient-to-r from-slate-50 to-white px-3 py-2">
      <div className="flex shrink-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
        <BarChart3 className="h-3.5 w-3.5 text-indigo-500" aria-hidden="true" />
        {title || "Visualization"}
      </div>
      {chips}
      {toolbar}
    </header>
  );

  if (fullscreen) {
    // Fullscreen overlay — covers the viewport, blurs the backdrop,
    // adopts a generous border-radius so the chart still feels like
    // a card rather than swallowing the entire window. Click-outside
    // doesn't dismiss because the overlay IS the chart; Esc handles
    // exit cleanly via the keydown listener above.
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Visualization (fullscreen)"
        className="fixed inset-0 z-50 flex flex-col bg-slate-900/40 p-4 backdrop-blur"
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-200">
          {header}
          <div
            ref={containerRef}
            className={`flex-1 overflow-auto p-4${dragging ? " select-none" : ""}`}
            style={{ cursor: panCursor }}
            onMouseDown={onPanDown}
            onMouseMove={onPanMove}
            onMouseUp={onPanEnd}
            onMouseLeave={onPanEnd}
          >
            {/* Render the body without the inline overflow wrapper
                — the fullscreen container is itself scrollable, and
                wrapping it again would create a nested scroll
                container that traps wheel events.

                At scale=1 give the wrapper the parent's full height
                so children with h-full (DeckGlRenderer, ECharts) fill
                the viewer instead of collapsing to their min-h-360px
                fallback. At scale > 1 fall back to content sizing so
                the scaled content overflows and the outer scroll
                container handles the pan. */}
            <div
              style={{
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                width: scale > 1 ? "max-content" : undefined,
                height: scale > 1 ? "max-content" : "100%",
                transition: "transform 120ms ease-out",
              }}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <figure
      // Gradient ring + soft shadow give the bubble a more polished
      // edge than the previous flat border. The padding lives on the
      // inner panel so the gradient ring is fully exposed at the
      // viewport edge.
      className="overflow-hidden rounded-xl bg-gradient-to-br from-indigo-100 via-slate-100 to-emerald-100 p-px shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="rounded-[11px] bg-white">
        {header}
        <div className="p-3">{body}</div>
      </div>
    </figure>
  );
}
